import { useState, useEffect, useCallback } from "react";
import { fetchQualityScores, fetchQualityTrend } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

// ── Score badge ──────────────────────────────────────────────────────────────

function ScoreBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  const color =
    value >= 4
      ? "bg-green-100 text-green-800 border-green-200"
      : value === 3
      ? "bg-yellow-100 text-yellow-800 border-yellow-200"
      : "bg-red-100 text-red-800 border-red-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${color}`}>
      {value}
    </span>
  );
}

// ── Overall badge (numeric, same colour scale) ───────────────────────────────

function OverallBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  const num = parseFloat(value);
  const color =
    num >= 4
      ? "bg-green-100 text-green-800 border-green-200"
      : num >= 3
      ? "bg-yellow-100 text-yellow-800 border-yellow-200"
      : "bg-red-100 text-red-800 border-red-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${color}`}>
      {num.toFixed(2)}
    </span>
  );
}

// ── Chart line colours ────────────────────────────────────────────────────────

const LINE_COLORS = {
  avg_overall:            "#6366f1",
  avg_helpfulness:        "#22c55e",
  avg_tone:               "#f59e0b",
  avg_escalation_correct: "#ef4444",
};

const LINE_LABELS = {
  avg_overall:            "Gesamt",
  avg_helpfulness:        "Hilfreich",
  avg_tone:               "Ton",
  avg_escalation_correct: "Eskalation",
};

const PAGE_SIZE = 20;

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Quality() {
  const [days, setDays] = useState(30);
  const [trend, setTrend] = useState(null);
  const [scores, setScores] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trendData, scoresData] = await Promise.all([
        fetchQualityTrend(days),
        fetchQualityScores(page, PAGE_SIZE),
      ]);
      setTrend(trendData.days || []);
      setScores(scoresData.items || []);
      setTotal(scoresData.total || 0);
    } catch (err) {
      setError("Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [days, page]);

  useEffect(() => {
    load();
  }, [load]);

  const openDrawer = (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-semibold text-xl">Qualität</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Automatische Bewertung aller Bot-Antworten durch Haiku
          </p>
        </div>
        <select
          className="text-sm border border-border rounded-lg px-3 py-1.5 bg-white"
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}
        >
          <option value={7}>7 Tage</option>
          <option value={30}>30 Tage</option>
          <option value={90}>90 Tage</option>
        </select>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Trend chart */}
      <div className="rounded-2xl border border-border-soft bg-white p-4 md:p-6">
        <h2 className="font-display font-semibold text-base mb-4">Trend</h2>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Lade…</div>
        ) : !trend || trend.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
            Noch keine Bewertungen vorhanden.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend} margin={{ top: 4, right: 16, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={(d) => new Date(d).toLocaleDateString("de-DE", { month: "numeric", day: "numeric" })}
              />
              <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => [value, LINE_LABELS[name] || name]}
                labelFormatter={(d) => new Date(d).toLocaleDateString("de-DE")}
              />
              <Legend formatter={(name) => LINE_LABELS[name] || name} />
              {Object.entries(LINE_COLORS).map(([key, color]) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Score table */}
      <div className="rounded-2xl border border-border-soft bg-white overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-border-soft flex items-center justify-between">
          <h2 className="font-display font-semibold text-base">Bewertungen</h2>
          <span className="text-xs text-muted-foreground">{total} gesamt</span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Lade…</div>
        ) : !scores || scores.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Noch keine Bewertungen vorhanden.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-soft bg-secondary/30">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs">Datum</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs">Nachricht</th>
                    <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs">Hilfreich</th>
                    <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs">Ton</th>
                    <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs">Eskalation</th>
                    <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs">Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {scores.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border-soft last:border-0 hover:bg-secondary/20 cursor-pointer transition-colors"
                      onClick={() => openDrawer(row)}
                    >
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {row.judged_at
                          ? new Date(row.judged_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <span className="line-clamp-1 text-foreground/80">{row.message_preview || "—"}</span>
                      </td>
                      <td className="px-3 py-3 text-center"><ScoreBadge value={row.helpfulness} /></td>
                      <td className="px-3 py-3 text-center"><ScoreBadge value={row.tone} /></td>
                      <td className="px-3 py-3 text-center"><ScoreBadge value={row.escalation_correct} /></td>
                      <td className="px-3 py-3 text-center"><OverallBadge value={row.overall} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-border-soft flex items-center justify-between">
                <button
                  className="text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-secondary disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Zurück
                </button>
                <span className="text-xs text-muted-foreground">
                  Seite {page} / {totalPages}
                </span>
                <button
                  className="text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-secondary disabled:opacity-40"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Weiter
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drill-down drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedRow && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle>Bewertungsdetail</SheetTitle>
              </SheetHeader>

              {/* Scores summary */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                {[
                  { label: "Hilfreich", value: selectedRow.helpfulness },
                  { label: "Ton", value: selectedRow.tone },
                  { label: "Eskalation", value: selectedRow.escalation_correct },
                  { label: "Gesamt", value: selectedRow.overall, isOverall: true },
                ].map(({ label, value, isOverall }) => (
                  <div key={label} className="rounded-xl border border-border-soft bg-secondary/30 p-3 text-center">
                    <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
                    {isOverall ? <OverallBadge value={value} /> : <ScoreBadge value={value} />}
                  </div>
                ))}
              </div>

              {/* Reasoning */}
              {selectedRow.reasoning && (
                <div className="rounded-xl border border-border-soft bg-secondary/20 p-3 mb-5">
                  <div className="text-xs text-muted-foreground mb-1">Begründung</div>
                  {/* Plain text only — never innerHTML */}
                  <p className="text-sm whitespace-pre-wrap">{selectedRow.reasoning}</p>
                </div>
              )}

              {/* Message */}
              <div className="mb-4">
                <div className="text-xs text-muted-foreground mb-1">Kundennachricht</div>
                <div className="rounded-xl border border-border-soft bg-secondary/20 p-3 text-sm whitespace-pre-wrap">
                  {selectedRow.message_full || "—"}
                </div>
              </div>

              {/* Bot reply */}
              <div className="mb-4">
                <div className="text-xs text-muted-foreground mb-1">Bot-Antwort</div>
                <div className="rounded-xl border border-border-soft bg-secondary/20 p-3 text-sm whitespace-pre-wrap">
                  {selectedRow.bot_reply_full || "—"}
                </div>
              </div>

              {/* Meta */}
              <div className="text-[10px] text-muted-foreground space-y-0.5">
                <div>Bewertet: {selectedRow.judged_at ? new Date(selectedRow.judged_at).toLocaleString("de-DE") : "—"}</div>
                <div>Modell: {selectedRow.model || "—"}</div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
